import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "context";
import { useTenantQuery } from "components/Auth/AuthenticatedQueryProvider";
import FullPageSpinner from "components/FullPageSpinner";
import { useAuth } from "./AuthContext";
import { useTenant } from "../../context/TenantContext";

const ContextUpdateWrapper = ({ profileData }) => {
  const { updateProfile } = useAuth();
  const { setTenant } = useAppContext();
  const { currentTenant } = useTenant();

  useEffect(() => {
    if (profileData) {
      // Make sure profileData is truthy before we update it
      if (profileData.id) {
        updateProfile(profileData);
        
        // Likewise with tenant - only if it exists and isn't already set
        if (currentTenant) {
          setTenant(currentTenant);
        }
      }
    }
  }, [profileData, updateProfile, setTenant, currentTenant]);

  return null;
};

const ContextComposer = ({ children }) => {
  const { currentTenant } = useTenant();

  // First query to get initial profile data
  const {
    data: basicProfile,
    isLoading: isLoadingInitial,
    error: initialError,
  } = useQuery({
    queryKey: ["profile"],
    staleTime: 0,
    retry: 3,
    refetchOnWindowFocus: false,
  });

  // Only make the user-roles query once we have the tenant and profile
  const {
    data: rolesResponse,
    isLoading: isLoadingRoles,
    error: rolesError,
  } = useQuery({
    ...useTenantQuery([`security/user-roles/${basicProfile?.id}`, currentTenant]),
    // Only execute this query when we have an actual profile and tenant
    enabled: !!basicProfile?.id && !!currentTenant && !isLoadingInitial,
  });

  const profileData = basicProfile
    ? {
        ...basicProfile,
        ...(rolesResponse ? { roles: rolesResponse } : {}),
      }
    : null;

  if ((isLoadingInitial) || (basicProfile && isLoadingRoles)) {
    return <FullPageSpinner />;
  }

  if (initialError) {
    console.error("Error loading basic profile:", initialError);
    return <div>Error loading profile: {initialError.message}</div>;
  }

  if (rolesError) {
    console.error("Error loading user roles:", rolesError);
    return <div>Error loading user roles: {rolesError.message}</div>;
  }

  return (
    <>
      {profileData && (
        <ContextUpdateWrapper
          profileData={profileData}
        />
      )}
      {children}
    </>
  );
};

export default ContextComposer;
